import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { SsoElement } from '../components/sso-base.js';
import {
  loginWithPasskey,
  webauthnSupported,
  isPasskeyUnavailableError,
} from '../auth/webauthn.js';
import { resolveMfaKinds } from '../auth/mfa-kinds.js';
import { MfaKind } from '../api/types.js';
import { navigate } from '../router.js';
import '../components/sso-card.js';
import '../components/sso-button.js';
import '../components/sso-alert.js';

/**
 * `<passkey-login-page>` — WebAuthn get() assertion ceremony (SSO-MOCKUPS.md §5b).
 *
 * Confirms identity with an existing passkey via `loginWithPasskey`, which returns the server's
 * next-step instruction (possibly a terminal redirect).
 *
 * Fallbacks depend on what the user actually has enrolled: an authenticator code is offered only
 * when a TOTP method exists; a passkey-only user is pointed at their recovery codes instead
 * (posting a code to the TOTP endpoint would fail with `no-totp-method` and never succeed).
 *
 * The browser cannot tell us up front whether the user's passkey is on this device — WebAuthn only
 * reports a generic "not allowed" after the prompt (privacy). So the page explains the requirement
 * before the prompt and, when the prompt comes back empty, says so explicitly.
 */
@customElement('passkey-login-page')
@localized()
export class PasskeyLoginPage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      .hero {
        display: grid;
        justify-items: center;
        gap: var(--sso-space);
        text-align: center;
      }
      .badge {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background: color-mix(in srgb, var(--sso-color-brand) 14%, var(--sso-color-surface));
        display: grid;
        place-items: center;
        font-size: 2rem;
      }
      .hint {
        color: var(--sso-color-muted);
        font-size: 0.9rem;
        margin: 0;
      }
      .links {
        display: grid;
        gap: 0.5rem;
        margin-top: var(--sso-space);
        text-align: center;
        font-size: 0.9rem;
      }
    `,
  ];

  @state() private error = '';
  @state() private busy = false;
  /** Whether the user also has an authenticator app enrolled; undefined until resolved. */
  @state() private hasTotp?: boolean;

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    try {
      this.hasTotp = (await resolveMfaKinds(this.flowContext)).includes(MfaKind.Totp);
    } catch {
      // Unknown ⇒ be conservative and do NOT offer the TOTP screen (a wrong guess there costs a
      // pointless failed attempt); recovery codes always work.
      this.hasTotp = false;
    }

    if (!webauthnSupported()) {
      // Passkeys cannot be used in this browser at all — go straight to the usable fallback.
      navigate(this.hasTotp ? '/2fa/totp' : '/2fa/totp?recovery=1&reason=passkey-unsupported');
    }
  }

  override firstUpdated(): void {
    this.focusFirstControl();
  }

  private async authenticate(): Promise<void> {
    this.busy = true;
    this.error = '';
    try {
      const r = await loginWithPasskey();
      if (r.next === 'complete') {
        this.complete();
        return;
      }
      this.error = this.hasTotp
        ? msg('Could not verify your passkey. Please try again or use an authenticator code.')
        : msg('Could not verify your passkey. Please try again or use a recovery code.');
    } catch (err) {
      if (isPasskeyUnavailableError(err)) {
        this.error = this.hasTotp
          ? msg(
              'No passkey was found on this device, or the request was cancelled. Try again, or use an authenticator code.',
            )
          : msg(
              'No passkey was found on this device, or the request was cancelled. Try again on the device where you set up your passkey, or use a recovery code.',
            );
      } else {
        this.error = this.hasTotp
          ? msg('Could not verify your passkey. Please try again or use an authenticator code.')
          : msg('Could not verify your passkey. Please try again or use a recovery code.');
      }
    } finally {
      this.busy = false;
    }
  }

  /** Second factor satisfied — follow the launcher-initiated return target if any, else launcher. */
  private complete(): void {
    const returnUri = this.resolveReturnUri();
    if (returnUri) {
      this.redirect(returnUri);
      return;
    }
    navigate('/');
  }

  private renderFallbackLink() {
    if (this.hasTotp === undefined) return nothing;
    if (this.hasTotp) {
      return html`
        <a
          href="/2fa/totp"
          @click=${(e: Event) => { e.preventDefault(); navigate('/2fa/totp'); }}>
          ${msg('Prefer a code? Use an authenticator code')}
        </a>
      `;
    }
    return html`
      <a
        href="/2fa/totp?recovery=1"
        @click=${(e: Event) => { e.preventDefault(); navigate('/2fa/totp?recovery=1'); }}>
        ${msg('No passkey on this device? Use a recovery code')}
      </a>
    `;
  }

  override render() {
    return html`
      <sso-card>
        <div class="hero">
          <h1>${msg('Sign in with a passkey')}</h1>
          <div class="badge" aria-hidden="true">🔑</div>
          <p>${msg('Confirm your identity with your passkey.')}</p>
          ${this.hasTotp === false
            ? html`<p class="hint">
                ${msg(
                  'Your passkey must be available on this device, or on a phone you can pair by scanning a QR code. If it is not, use one of your recovery codes.',
                )}
              </p>`
            : nothing}
        </div>
        ${this.error ? html`<sso-alert kind="error">${this.error}</sso-alert>` : nothing}
        <sso-button variant="primary" ?busy=${this.busy} @click=${this.authenticate}>
          ${msg('Continue with passkey')}
        </sso-button>
        <div class="links">
          ${this.renderFallbackLink()}
          <a href="/logout">${msg('Cancel / Sign out')}</a>
        </div>
      </sso-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'passkey-login-page': PasskeyLoginPage;
  }
}
